import { LitElement, html } from '@hh/global.js'

const DESKTOP_QUERY = '(min-width: 673px)'
const FEED_HOST_ID = 'wisepops-feed'
const PANEL_SELECTOR = '.feed-panel'
const PANEL_OPEN_CLASS = 'feed-panel--open'

/**
 * TMX-1363: turns the default Wisepops dropdown popup into a full-height
 * right-edge drawer on desktop, mirroring the Explore mega-menu. The panel
 * itself lives in an open shadow root Wisepops attaches to #wisepops-feed,
 * so this component watches for it and injects the desktop CSS (authored
 * in the <template> below the trigger) plus a click-to-close overlay.
 */
class WisepopsNotification extends LitElement {
  constructor() {
    super()
    this.desktopQuery = window.matchMedia(DESKTOP_QUERY)
    this.syncOverlay = this.syncOverlay.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    // Light-DOM children aren't guaranteed to exist yet in the constructor
    // (e.g. if this element is ever built up node-by-node rather than
    // parsed from static markup), so resolve the trigger here instead.
    this.trigger = this.querySelector('a, button')
    this.desktopQuery.addEventListener('change', this.syncOverlay)
    this.watchForFeed()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.desktopQuery.removeEventListener('change', this.syncOverlay)
    this.untrackAnchor()
    document.removeEventListener('keydown', this.handleKeydown)
    this.hostObserver?.disconnect()
    this.contentObserver?.disconnect()
    this.panelObserver?.disconnect()
    this.overlay?.remove()
    document.body.classList.remove('wisepops-notification-open')
    this.restoreFocus()
  }

  watchForFeed() {
    const existing = document.getElementById(FEED_HOST_ID)
    if (existing?.shadowRoot) {
      this.onFeedReady(existing.shadowRoot)
      return
    }

    this.hostObserver = new MutationObserver(() => {
      const feedHost = document.getElementById(FEED_HOST_ID)
      if (feedHost?.shadowRoot) {
        this.hostObserver.disconnect()
        this.onFeedReady(feedHost.shadowRoot)
      }
    })
    this.hostObserver.observe(document.body, { childList: true, subtree: true })
  }

  onFeedReady(shadowRoot) {
    this.shadowRootRef = shadowRoot
    this.injectPanelStyle()
    this.buildOverlay()
    this.watchPanelContent()
    // Listens on document rather than the shadow root: Wisepops replaces
    // .list-view__content's children on list-to-detail navigation, and if
    // the previously-focused node was inside it, focus can transiently land
    // outside this shadow tree entirely while that swap settles - a
    // shadow-root-scoped listener would silently stop firing for the rest
    // of that window.
    document.addEventListener('keydown', this.handleKeydown)
  }

  /** Relays the CSS authored in the liquid template into Wisepops' shadow root. */
  injectPanelStyle() {
    const template = this.querySelector('template[data-wisepops-panel-style]')
    if (!template || this.shadowRootRef.querySelector('style[data-wisepops-panel-style]'))
      return

    const style = template.content.cloneNode(true).querySelector('style')
    style.setAttribute('data-wisepops-panel-style', '')
    this.shadowRootRef.appendChild(style)
  }

  /**
   * The close affordance on hover is a custom cursor image (see the
   * .wisepops-notification__overlay:hover rule), matching the Explore
   * mega-menu's own .header-v2__menu-item--parent-overlay:hover pattern
   * (assets/section-header-v2.css) - not a positioned button, so there's
   * nothing here to keep in sync with the overlay's own open/close state.
   */
  buildOverlay() {
    this.overlay = document.createElement('div')
    this.overlay.className = 'wisepops-notification__overlay'
    this.overlay.addEventListener('click', () => this.trigger?.click())
    document.body.appendChild(this.overlay)
  }

  /**
   * The panel's internal tree renders asynchronously, and Wisepops replaces
   * .list-view__content's children whenever the user navigates back from the
   * detail view, wiping our injected header. So this keeps watching for as
   * long as the component is connected, re-injecting whenever needed instead
   * of binding once.
   */
  watchPanelContent() {
    const bindPanel = () => {
      if (this.panel?.isConnected) return
      const panel = this.shadowRootRef.querySelector(PANEL_SELECTOR)
      if (!panel) return
      this.panelObserver?.disconnect()
      this.panel = panel
      this.panelObserver = new MutationObserver(this.syncOverlay)
      this.panelObserver.observe(panel, { attributes: true, attributeFilter: ['class'] })
      this.syncOverlay()
    }

    const ensureHeading = () => {
      const listContent = this.shadowRootRef.querySelector('.list-view__content')
      if (listContent) this.buildHeading(listContent)
    }

    bindPanel()
    ensureHeading()

    this.contentObserver = new MutationObserver(() => {
      bindPanel()
      ensureHeading()
    })
    this.contentObserver.observe(this.shadowRootRef, { childList: true, subtree: true })
  }

  /**
   * Reuses Wisepops' own mobile title bar markup/classes (normally hidden
   * above 672px) so the desktop title + close button match mobile exactly.
   * Wisepops' own mobile trigger and title text differ ("Notifications" vs
   * "What's new"), so these come from their own translated attributes
   * rather than the trigger's aria-label.
   */
  buildHeading(listContent) {
    if (listContent.querySelector('.list-view__header')) return

    const label = this.getAttribute('data-wisepops-heading') || "What's new"
    const closeLabel = this.getAttribute('data-wisepops-close-label') || 'Close notifications'

    const header = document.createElement('div')
    header.className = 'list-view__header'
    header.setAttribute('data-wisepops-notification-header', '')

    const title = document.createElement('h3')
    title.className = 'list-view__title'
    title.textContent = label

    const closeButton = document.createElement('button')
    closeButton.type = 'button'
    closeButton.className = 'close-button list-view__close'
    closeButton.setAttribute('aria-label', closeLabel)
    closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    `
    closeButton.addEventListener('click', () => this.trigger?.click())

    header.append(title, closeButton)
    listContent.prepend(header)
  }

  /**
   * Drives everything that depends on the drawer's open state from one
   * place - overlay, scroll lock, anchor tracking, nav-menu closing and
   * focus - so they always agree with it. A dedicated class name is used for
   * the scroll lock (rather than the theme's shared .overflow-hidden
   * utility) so closing this drawer can't clobber another
   * independently-toggled overlay, like the Explore mega-menu, that happens
   * to be open at the same time.
   */
  syncOverlay() {
    const isOpen = !!this.panel?.classList.contains(PANEL_OPEN_CLASS)
    const showOverlay = isOpen && this.desktopQuery.matches
    const wasOpen = !!this.overlay?.classList.contains('is-open')
    if (showOverlay === wasOpen) return

    if (showOverlay) {
      this.trackAnchor()
      this.closeNavMenus()
    }
    this.overlay?.classList.toggle('is-open', showOverlay)
    document.body.classList.toggle('wisepops-notification-open', showOverlay)

    if (showOverlay) {
      // Move focus into the drawer so a keyboard user who opened it lands
      // inside. Wisepops also focuses its own dialog container
      // (role="dialog", tabindex="-1") on open, asynchronously; whichever
      // lands last wins, and both are inside the panel.
      this.previouslyFocusedElement = document.activeElement
      this.restoreFocusOnClose = !!this.previouslyFocusedElement?.matches?.(':focus-visible')
      this.getFocusableElements()[0]?.focus()
    } else {
      this.untrackAnchor()
      this.restoreFocus()
    }
  }

  /**
   * Starts the drawer and its overlay right under the main header, like the
   * Explore / Blog mega-menus (.header-v2__nav) do, instead of covering it,
   * so it reads as part of the nav and the other nav items stay clickable.
   * Measured from the header's real bottom edge rather than the theme's
   * --hh-total-header-height variable, because the sticky header hides its
   * promo/announcement bars once the page is scrolled, which that variable
   * doesn't track. The 1px overlap matches the Explore panel, whose top
   * border sits over the header's bottom edge.
   */
  updateAnchor() {
    const header = document.querySelector('[data-main-header]')
    const top = header
      ? Math.max(0, Math.round(header.getBoundingClientRect().bottom) - 1)
      : 0
    if (top === this.anchorTop) return
    this.anchorTop = top
    // Set on the overlay (light DOM) and on Wisepops' shadow host, since a
    // custom property only reaches the panel through the host's subtree.
    this.overlay?.style.setProperty('--wisepops-notification-top', `${top}px`)
    this.shadowRootRef?.host.style.setProperty('--wisepops-notification-top', `${top}px`)
  }

  /**
   * Keeps the anchor in step with the header for as long as the drawer is
   * open. The header's bottom edge can move without any resize event - a
   * dismissed promo bar, an announcement that wraps, the sticky header
   * sliding - and sampling it once on open left the drawer and overlay
   * overlapping the header or leaving a gap. Polling per frame is
   * cause-agnostic, and only runs while the drawer is open.
   */
  trackAnchor() {
    this.untrackAnchor()
    const tick = () => {
      this.updateAnchor()
      this.anchorFrame = requestAnimationFrame(tick)
    }
    tick()
  }

  untrackAnchor() {
    cancelAnimationFrame(this.anchorFrame)
  }

  /**
   * Only one nav panel should be open at a time. The other direction needs
   * nothing: clicking another nav item is an outside click, which Wisepops
   * itself closes on. But the bell's own click never bubbles up to the
   * header's document-level click handler (observed: it stops before the
   * document's bubble phase), so an open Explore / Blog / etc. menu isn't
   * closed by it and would sit stale under the drawer.
   *
   * .active-group-menu has to be cleared first, exactly as hh-header's own
   * outside-click handler does before closing: once a menu has been open for
   * 500ms the theme puts that class on its <ul>, and
   * `.active-group-menu .header-v2__nav` sets `transition: none; transform:
   * none` (so switching between open menus is instant). Calling closeMenu()
   * alone leaves that in place, and the panel just vanishes instead of
   * sliding out the way it does when its overlay is clicked.
   *
   * Does nothing unless a mega menu is actually open: closeMenu() ends in
   * applyState(), which switches body's overflow-hidden off whenever no menu
   * is active, and that class is shared (search, filter drawer, modals, ...) -
   * so an unconditional call would release another feature's scroll lock.
   *
   * The open menu is detected via aria-expanded on the mega-menu trigger
   * (hh-header applyState sets that on .js-header-mega-menu-trigger, not on
   * the panel). The group to clear is still the open panel's <ul>, where
   * hh-header adds .active-group-menu.
   */
  closeNavMenus() {
    const header = document.querySelector('hh-header')
    const openTrigger = header?.querySelector(
      '.js-header-mega-menu-trigger[aria-expanded="true"]'
    )
    if (!openTrigger) return
    openTrigger
      .closest('.js-header-mega-menu-parent')
      ?.querySelector('[data-mega-menu-subnav]')
      ?.closest('ul')
      ?.classList.remove('active-group-menu')
    header?.closeMenu?.()
  }

  getFocusableElements() {
    if (!this.panel) return []
    return Array.from(
      this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null)
  }

  /**
   * Gives focus back to the bell only when the drawer was opened from the
   * keyboard. After a mouse open, focus left on the bell would keep the
   * transparent header filled (the theme's .bg-background:focus-within
   * rule) after the pointer leaves, unlike closing the Explore / Blog
   * menus, so it is dropped instead - along with any focus still inside
   * the drawer (opening moved it there), so the next Tab or Enter doesn't
   * act on a control that is now off-screen.
   */
  restoreFocus() {
    const previous = this.previouslyFocusedElement
    if (this.restoreFocusOnClose && previous?.isConnected) {
      previous.focus()
    } else {
      this.shadowRootRef?.activeElement?.blur()
      if (this.trigger?.matches(':focus') && !this.trigger.matches(':focus-visible')) {
        this.trigger.blur()
      }
    }
    this.previouslyFocusedElement = null
    this.restoreFocusOnClose = false
  }

  /**
   * Escape closes the drawer. There is deliberately no Tab trap and the
   * panel isn't aria-modal: the overlay starts under the header so the nav
   * stays clickable, which makes this a non-modal flyout like the theme's own
   * mega-menus - confining Tab to the panel would leave those visible header
   * controls pointer-only. An earlier version trapped Tab inside the panel,
   * which was right while the overlay covered the whole page.
   */
  handleKeydown(event) {
    if (event.key !== 'Escape') return
    if (!this.desktopQuery.matches || !this.overlay?.classList.contains('is-open')) return
    this.trigger?.click()
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define('wisepops-notification', WisepopsNotification)
